import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";

import { Autocomplete, Box, Grid, InputAdornment, MenuItem, styled, Typography } from "@mui/material";
import dayjs from "dayjs";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import DrawerCustom from "../../../../shared/general/Drawer";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import DatePickerCustom from "../../../../shared/date/datePicker";
import { getCompanyOrganization, getManagerByidCompany } from "../../../../../../actions/company";
import { createEmployeePosition, getEmployeePositionChange, getEmployeeProfile, updateEmployeePosition } from "../../../../../../actions/employee";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
})

const DrawerEdit = (props) => {

  const { drawerConfig, handleClose } = props;

  const dispatch = useDispatch();
  const { t, i18n} = useTranslation();

  const { result: companyOrganization } = useSelector((state) => state.companyOrganization);
  const { result: companyContract } = useSelector((state) => state.vendorContract);
  const { result: managerList } = useSelector((state) => state.manager);
  const { result: employeeProfile } = useSelector((state) => state.employeeProfile);

  const validateYupSchema = yup.object({
    company: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
    positions: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
    start: yup.date().nullable().required(`${t("ThisFieldIsRequired")}`),
    managerLV1: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
    managerLV2: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
  });
  
  const defaultValues = {
    company: null,
    positions: null,
    start: null,
    managerLV1: null,
    managerLV2: null,
    remark: "",
  }

  const useHookForm = useForm({
    defaultValues: defaultValues,
    mode: "all",
    resolver: yupResolver(validateYupSchema),
  })

  const onSubmit = async (data) => {

    let result = null;

    const formData = {
      idEmp: employeeProfile.idEmp,
      idPositions: data.positions.idPositions,
      start: dayjs(data.start).format("YYYY-MM-DD"),
      idManagerLV1: data.managerLV1.idManagerEmployee,
      idManagerLV2: data.managerLV2.idManagerEmployee,
      remark: data.remark || null,
    }

    if(!drawerConfig.isEdit){
      result = await dispatch(createEmployeePosition(formData));
    } else {
      result = await dispatch(updateEmployeePosition(drawerConfig.data.idEmployeePosition, formData));
    }

    if(result && result.status === 200) {
      handleClose();
      dispatch(openNotificationAlert({
        type: "success",
        message: t("DataSaveSuccessful"),
      }));
      dispatch(getEmployeeProfile(employeeProfile.idEmp));
      dispatch(getEmployeePositionChange(employeeProfile.idEmp));
      
    } else {
      dispatch(openNotificationAlert({
        type: "error",
        message: t("AnErrorOccurred")
      }));
    }

  }

  useEffect(() => {

    if(drawerConfig.isOpen){
      const init = async () => {
        if (drawerConfig.isEdit) {
  
          let foundCompany = null;
          let foundPositions = null;
          let foundManagerLV1 = null;
          let foundManagerLV2 = null;
  
          foundCompany = companyContract.find((x) => x.idCompany === drawerConfig.data.idCompany);
  
          if (foundCompany) {
            const positionList = await dispatch(getCompanyOrganization(foundCompany.idCompany)).then(res => res.data);
            const managerList = await dispatch(getManagerByidCompany(foundCompany.idCompany)).then(res => res.data);

            foundPositions = positionList.find(x => x.idPositions === drawerConfig.data.idPositions)
            foundManagerLV1 = managerList.find(x => x.idManagerEmployee === drawerConfig.data.idManagerLV1)
            foundManagerLV2 = managerList.find(x => x.idManagerEmployee === drawerConfig.data.idManagerLV2)
          }
  
          useHookForm.reset({
            company: foundCompany || null,
            positions: foundPositions || null,
            managerLV1: foundManagerLV1 || null,
            managerLV2: foundManagerLV2 || null,
            start: drawerConfig.data.start || null,
            remark: drawerConfig.data.remark || "",
          });
        } else {
          useHookForm.reset(defaultValues);
        }
      };
  
      init();
    }
    
  }, [drawerConfig.isOpen])

  const handleChangeCompany = (idCompany) => {
    dispatch(getCompanyOrganization(idCompany));
    dispatch(getManagerByidCompany(idCompany));
  };

  return (
    <DrawerCustom
      title={`${t("Edit")} ${t("Transfer")}/${t("Move")}`}
      anchor={"right"}
      open={drawerConfig.isOpen}
      onClose={handleClose}
      PaperProps={{style: {maxWidth: 550, width: "100%"}}}
    >
      <StyledRoot>
        <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("Company")}</Typography>
              <Controller
                name="company"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={companyContract? companyContract: []}
                    getOptionLabel={(option) => option.companyName}
                    isOptionEqualToValue={(option, value) => option.idCompany === value.idCompany}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.idCompany}>
                        <Box>
                          <Typography>{option.companyName}</Typography>
                        </Box>
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        placeholder={`${t("Search")}`}
                        onBlur={field.onBlur}
                        error={fieldState.error ? true : false}
                        helperText={fieldState.error ? fieldState.error.message : null}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                      if(value) {
                        handleChangeCompany(value.idCompany);
                        useHookForm.setValue("positions", null);
                        useHookForm.setValue("managerLV1", null);
                        useHookForm.setValue("managerLV2", null);
                      }
                    }}
                    noOptionsText="ไม่พบข้อมูล"
                    disableClearable
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("Position")}</Typography>
              
              <Controller
                name="positions"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={companyOrganization? companyOrganization: []}
                    getOptionLabel={(option) => option.positionsName}
                    isOptionEqualToValue={(option, value) => option.idPositions === value.idPositions}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.idPositions}>
                        <Box>
                          <Typography>{option.positionsName}</Typography>
                          <Typography color="text.secondary" variant="caption">
                            {option.divisionName || "-"}/{option.departmentName || "-"}/{option.sectionName || "-"}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        placeholder={`${t("Search")}`}
                        onBlur={field.onBlur}
                        error={fieldState.error? true: false}
                        helperText={fieldState.error? fieldState.error.message: null}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    noOptionsText="ไม่พบข้อมูล"
                    disableClearable
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("ManagerLevel")} 1</Typography>
              
              <Controller
                name="managerLV1"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={managerList? managerList: []}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.idManagerEmployee === value.idManagerEmployee}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.idManagerEmployee}>
                        <Box>
                          <Typography>{option.name}</Typography>
                          <Typography color="text.secondary" variant="body2">
                            {option.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        placeholder={`${t("Search")}`}
                        onBlur={field.onBlur}
                        error={fieldState.error? true: false}
                        helperText={fieldState.error? fieldState.error.message: null}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    noOptionsText="ไม่พบข้อมูล"
                    disableClearable
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("ManagerLevel")} 2</Typography>
              
              <Controller
                name="managerLV2"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={managerList? managerList: []}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.idManagerEmployee === value.idManagerEmployee}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.idManagerEmployee}>
                        <Box>
                          <Typography>{option.name}</Typography>
                          <Typography color="text.secondary" variant="body2">
                            {option.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        placeholder={`${t("Search")}`}
                        onBlur={field.onBlur}
                        error={fieldState.error? true: false}
                        helperText={fieldState.error? fieldState.error.message: null}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                      // if (value) {
                        // dispatch(getAllPositions({ idCompany: value.idCompany }));
                        // fetchingCompanyLocationList(value.idCompany);
                      // }
                      // if (value) {
                      //   useHookForm.setValue("positions", null);
                      //   useHookForm.setValue("managerLV1", null);
                      //   useHookForm.setValue("managerLV2", null);
                      // }
                    }}
                    noOptionsText="ไม่พบข้อมูล"
                    disableClearable
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("EffectiveDate")}</Typography>
              
              <Controller
                name="start"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <DatePickerCustom
                    {...field}
                    views={["year", "month", "day"]}
                    inputFormat="DD MMMM YYYY"
                    openTo="day"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: t("SelectDate"),
                          readOnly: true
                        }}
                        onBlur={field.onBlur}
                        error={fieldState.error? true: false}
                        helperText={fieldState.error? fieldState.error.message: null}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="14px" color="text.secondary" paddingBottom="4px">{t("Remark")}</Typography>
              
              <Controller
                name="remark"
                control={useHookForm.control}
                render={({field, fieldState}) => (
                  <TextFieldTheme
                    {...field}
                    multiline
                    minRows={3}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          style={{
                            alignSelf: "flex-end",
                            fontSize: "12px",
                            userSelect: "none",
                          }}
                        >
                          {500 - field.value.length}
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => {
                      field.onChange(event.target.value.slice(0, 500));
                    }}
                    error={fieldState.error? true: false}
                    helperText={fieldState.error? fieldState.error.message: null}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} container justifyContent="space-between">
              <ButtonBlue onClick={handleClose}>
                {t("Cancel")}
              </ButtonBlue>
              <ButtonBlue variant="contained" type="submit" disabled={useHookForm.formState.isSubmitting}>
                {t("Save")}
              </ButtonBlue>
            </Grid>
          </Grid>
        </form>
      </StyledRoot>
    </DrawerCustom>
  )
}

export default DrawerEdit;